/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import useDropdownPosition from '../../hooks/useDropdownPosition';

function ChevronIcon({ open }: { open: boolean }) {
    return (
        <svg
            className={clsx(
                'w-4 h-4 flex-shrink-0 transform transition-transform duration-150',
                open && 'rotate-180'
            )}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
    );
}

export default function Select({
    label,
    disabled,
    options,
    name,
    onChange,
    error,
    errorMessage,
    placeholder,
    width,
    selected,
    compact,
}: {
    label?: React.ReactNode;
    disabled?: boolean;
    options: { value: string; label: string }[];
    name?: string;
    error?: boolean;
    errorMessage?: string;
    placeholder?: string;
    width?: string;
    selected?: string;
    // Tighter vertical padding for use inside condensed read-only info panels,
    // where the surrounding rows are plain text rather than full-height inputs.
    compact?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [internalSelected, setInternalSelected] = useState(selected);
    const containerRef = useRef<HTMLDivElement>(null);
    const { triggerRef, menuRef, position } = useDropdownPosition<HTMLButtonElement>(isOpen);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideContainer = containerRef.current?.contains(target);
            const insideMenu = menuRef.current?.contains(target);
            if (!insideContainer && !insideMenu) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selected !== undefined) {
            setInternalSelected(selected);
        }
    }, [selected]);

    // Falls back to internally-tracked selection for call sites that don't
    // wire the `selected` prop back from their own state - a native <select>
    // keeps the chosen option visible on its own, this custom dropdown does not.
    const currentValue = selected !== undefined ? selected : internalSelected;
    const selectedOption = options.find((option) => option.value === currentValue);

    const handleSelect = (value: string) => {
        setIsOpen(false);
        setInternalSelected(value);
        if (onChange) {
            onChange(({
                target: { value, name },
            } as unknown) as React.ChangeEvent<HTMLSelectElement>);
        }
    };

    return (
        <div
            className={width || 'w-full'}
            ref={containerRef}
            onKeyDown={(event) => {
                if (event.key === 'Escape') setIsOpen(false);
            }}
        >
            {label && <p className="text-sm font-medium pb-1.5 text-label">{label}</p>}
            <div className="relative">
                <button
                    ref={triggerRef}
                    type="button"
                    disabled={disabled}
                    onClick={() => setIsOpen((prev) => !prev)}
                    className={clsx(
                        'flex items-center justify-between gap-2 w-full border rounded-lg text-sm pl-3 pr-3 text-left transition-colors focus:outline-none',
                        compact ? 'py-1' : 'py-2',
                        disabled
                            ? 'cursor-not-allowed bg-border-subtle text-foreground-muted border-border'
                            : 'bg-surface-muted text-foreground border-border hover:border-border-strong focus:bg-surface focus:border-border-strong'
                    )}
                >
                    <span className={clsx('truncate', !selectedOption && 'text-foreground-muted')}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                    <ChevronIcon open={isOpen} />
                </button>
                {isOpen &&
                    !disabled &&
                    position &&
                    ReactDOM.createPortal(
                        <div
                            ref={menuRef}
                            style={{
                                position: 'fixed',
                                top: position.top,
                                left: position.left,
                                width: position.width,
                            }}
                            className="z-50 max-h-60 overflow-y-auto bg-surface border border-border rounded-lg shadow-lg py-1"
                        >
                            {options.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    className={clsx(
                                        'block w-full text-left px-3 py-2 text-sm transition-colors focus:outline-none',
                                        option.value === currentValue
                                            ? 'bg-accent-soft text-orange-500 font-medium'
                                            : 'text-foreground hover:bg-surface-muted'
                                    )}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>,
                        document.body
                    )}
            </div>
            {error && <p className="mt-1 text-xs text-danger">{errorMessage}</p>}
        </div>
    );
}
